import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { MapsAPILoader } from './maps-api-loader';

@Component({
  selector: 'agm-map',
  template: '<div #mapHost class="agm-map-host" style="width:100%;height:100%;min-height:280px"></div>',
  styles: [
    `
      :host {
        display: block;
        width: 100%;
        height: 100%;
      }
    `
  ]
})
export class AgmMap implements AfterViewInit, OnChanges, OnDestroy {
  @Input() latitude = 25.1576349;
  @Input() longitude = 55.2262386;
  @Input() zoom = 12;
  @Input() restriction: google.maps.MapRestriction | null = null;

  @Output() mapClick = new EventEmitter<{ coords: { lat: number; lng: number } }>();

  @ViewChild('mapHost', { static: true }) mapHost!: ElementRef<HTMLDivElement>;

  private map: google.maps.Map | null = null;
  private marker: google.maps.Marker | null = null;
  private clickListener: google.maps.MapsEventListener | null = null;

  constructor(
    private readonly zone: NgZone,
    private readonly mapsApiLoader: MapsAPILoader
  ) {}

  ngAfterViewInit(): void {
    void this.mapsApiLoader.load().then(() => this.initMap());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.map) return;
    if (changes['latitude'] || changes['longitude']) {
      const lat = Number(this.latitude);
      const lng = Number(this.longitude);
      if (Number.isFinite(lat) && Number.isFinite(lng)) {
        const pos = { lat, lng };
        this.map.setCenter(pos);
        this.marker?.setPosition(pos);
      }
    }
    if (changes['zoom'] && this.map) {
      const z = Number(this.zoom);
      if (Number.isFinite(z)) this.map.setZoom(z);
    }
    if (changes['restriction'] && this.map) {
      this.map.setOptions({ restriction: this.restriction || undefined });
    }
  }

  ngOnDestroy(): void {
    if (this.clickListener) {
      google.maps.event.removeListener(this.clickListener);
      this.clickListener = null;
    }
    this.marker = null;
    this.map = null;
  }

  /** @see legacy AGM `triggerResize` */
  triggerResize(_recenter: boolean = false): void {
    if (!this.map) return;
    const center = this.map.getCenter();
    google.maps.event.trigger(this.map, 'resize');
    if (center) this.map.setCenter(center);
  }

  private initMap(): void {
    if (typeof google === 'undefined' || !google.maps) return;
    this.zone.runOutsideAngular(() => {
      const lat = Number(this.latitude);
      const lng = Number(this.longitude);
      const center =
        Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : { lat: 25.1576349, lng: 55.2262386 };
      const el = this.mapHost.nativeElement;
      this.map = new google.maps.Map(el, {
        center,
        zoom: Number.isFinite(Number(this.zoom)) ? Number(this.zoom) : 12,
        restriction: this.restriction || undefined,
        streetViewControl: false,
        mapTypeControl: false
      });
      this.marker = new google.maps.Marker({ position: center, map: this.map });
      this.clickListener = this.map.addListener('click', (e: google.maps.MapMouseEvent) => {
        if (e.latLng == null) return;
        const coords = { lat: e.latLng.lat(), lng: e.latLng.lng() };
        this.zone.run(() => {
          this.latitude = coords.lat;
          this.longitude = coords.lng;
          this.marker?.setPosition(coords);
          this.mapClick.emit({ coords });
        });
      });
    });
  }
}

